/**
 * Обгортка браузерної бібліотеки підпису ІІТ (euscp) для КЕП.
 *
 * Бібліотека вендорена у /public/eusign/ (euutils → euscpt → euscpm → euscp,
 * ~7.6МБ) і вантажиться ЛІНИВО (динамічні <script>) лише коли користувач
 * відкриває панель підпису. Крипто-ресурси (CAs.json, CACertificates.p7b,
 * сертифікати ДПС) роздає backend: /api/v1/eusign/assets/*.
 *
 * Ключ НЕ покидає браузер: читання/підпис/шифрування — локально (WASM/asm.js);
 * назовні йдуть лише запити CMP/TSP/OCSP через наш проксі /api/v1/eusign/proxy
 * (формат ІІТ ProxyHandler) та вже зашифровані конверти.
 *
 * Джерело патернів — офіційне демо ІІТ js.sign.eu.iit.com.ua (euscptest.js).
 */

// Мінімальні типи для API euscp (бібліотека не має TS-типів).
export interface EuSignCA {
  issuerCNs: string[];
  address: string;
  ocspAccessPointAddress: string;
  ocspAccessPointPort: string;
  cmpAddress: string;
  tspAddress: string;
  tspAddressPort: string;
  certsInKey?: boolean;
}

export interface KeyOwnerInfo {
  subjCN: string;
  subjOrg?: string;
  subjTitle?: string;
  issuerCN: string;
  serial: string;
  subjDRFOCode?: string;
  subjEDRPOUCode?: string;
}

declare global {
  interface Window {
    EUSignCP?: () => any;
    EUSignCPModuleInitialized?: (ok: boolean) => void;
  }
}

const PROXY_URL = '/api/v1/eusign/proxy';
const ASSETS = '/api/v1/eusign/assets';
const SCRIPTS = ['euutils.js', 'euscpt.js', 'euscpm.js', 'euscp.js'];

let euSign: any = null;
let modulePromise: Promise<void> | null = null;
let casCache: EuSignCA[] | null = null;

function injectScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Не вдалося завантажити ${src}`));
    document.head.appendChild(s);
  });
}

/** Ліниве завантаження бібліотеки + ініціалізація крипто-модуля. */
export function loadEuSign(): Promise<void> {
  if (modulePromise) return modulePromise;
  modulePromise = (async () => {
    const ready = new Promise<void>((resolve, reject) => {
      window.EUSignCPModuleInitialized = (ok: boolean) => {
        if (ok) resolve();
        else reject(new Error('Криптографічну бібліотеку не ініціалізовано'));
      };
    });
    for (const f of SCRIPTS) {
      // послідовно: euscpm потребує euscpt; euscp сигналить через callback
      await injectScript(`/eusign/${f}`);
    }
    await ready;
    euSign = window.EUSignCP!();
    euSign.Initialize();
    euSign.SetJavaStringCompliant(true);
    euSign.SetCharset('UTF-8');

    // Налаштування за замовчуванням (патерн демо ІІТ setDefaultSettings)
    euSign.SetXMLHTTPProxyService(PROXY_URL);
    let st = euSign.CreateFileStoreSettings();
    st.SetPath('/certificates');
    st.SetSaveLoadedCerts(true);
    euSign.SetFileStoreSettings(st);
    euSign.SetProxySettings(euSign.CreateProxySettings());
    euSign.SetCMPSettings(euSign.CreateCMPSettings());
    euSign.SetLDAPSettings(euSign.CreateLDAPSettings());

    // НЕ вмикаємо глобальний offline (він ріже і CMP — читання ключа впаде
    // «Доступ до сервера ЦСК неможливий»). Натомість точково ВИМИКАЄМО лише
    // online-перевірку статусу OCSP і TSP-мітку часу: джерело помилок
    // «не вдалося отримати статус за OCSP» — недоступність responder'а АЦСК.
    // Статус сертифіката перевіряє САМ ДПС на приймальному шлюзі; CMP лишається
    // online (потрібен для читання ключа / витягу сертифікатів).
    const mode = euSign.CreateModeSettings();
    mode.SetOfflineMode(false);
    euSign.SetModeSettings(mode);
    const tsp = euSign.CreateTSPSettings();
    tsp.SetGetStamps(false);
    euSign.SetTSPSettings(tsp);
    const ocsp = euSign.CreateOCSPSettings();
    ocsp.SetUseOCSP(false);
    euSign.SetOCSPSettings(ocsp);

    // Сертифікати всіх АЦСК — у файлове сховище бібліотеки (для побудови ланцюга)
    const p7b = await fetchBytes(`${ASSETS}/CACertificates.p7b`);
    euSign.SaveCertificates(p7b);
  })();
  modulePromise.catch(() => { modulePromise = null; });
  return modulePromise;
}

async function fetchBytes(url: string): Promise<Uint8Array> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`HTTP ${r.status} при завантаженні ${url}`);
  return new Uint8Array(await r.arrayBuffer());
}

/** Перелік АЦСК (для селекта на панелі). */
export async function getCAs(): Promise<EuSignCA[]> {
  if (casCache) return casCache;
  const r = await fetch(`${ASSETS}/CAs.json`);
  if (!r.ok) throw new Error('Не вдалося завантажити перелік АЦСК');
  casCache = await r.json();
  return casCache!;
}

/**
 * Виставити CMP під обраний АЦСК. OCSP/TSP лишаються ВИМКНЕНІ (offline —
 * див. loadEuSign): статус сертифіката перевіряє ДПС на приймальному шлюзі.
 * CMP лишаємо online — ним дістаємо сертифікати з ключа, якщо їх там немає.
 */
function applyCASettings(ca: EuSignCA | null) {
  const useCMP = !!ca && !!ca.address && !!ca.cmpAddress;
  const cmp = euSign.CreateCMPSettings();
  cmp.SetUseCMP(useCMP);
  if (useCMP) {
    cmp.SetAddress(ca!.cmpAddress);
    cmp.SetPort('80');
  }
  euSign.SetCMPSettings(cmp);
}

/**
 * Зчитати особистий ключ (Key-6.dat / *.pfx / *.zs2 ...) з АВТОвизначенням АЦСК.
 *
 * ca — необов'язковий (можна null): АЦСК визначається сам. Спроби по черзі:
 *  1) сертифікати вшиті у ключ (нові ключі Дії/хмарні) — читаємо напряму;
 *  2) якщо ні — тягнемо сертифікати по CMP: спершу обраний АЦСК (якщо задано),
 *     інакше перебираємо УСІ CMP-адреси з CAs.json одним запитом
 *     (GetCertificatesByKeyInfo сам обере правильний видавець за ключем).
 *
 * Так користувачу НЕ треба вручну обирати видавця — просто ключ + пароль.
 */
export async function readPrivateKey(
  keyBytes: Uint8Array, password: string, ca: EuSignCA | null,
  fileName = '',
): Promise<KeyOwnerInfo> {
  await loadEuSign();
  applyCASettings(ca);
  resetSlot();  // лише слот бібліотеки — реєстр раніше зчитаних ключів НЕ чіпаємо
  try {
    // Крок 1: сертифікати у ключі
    euSign.ReadPrivateKeyBinary(keyBytes, password);
  } catch (e: any) {
    // Крок 2: дістати сертифікати по CMP (авто-перебір АЦСК)
    try {
      const keyInfo = euSign.GetKeyInfoBinary(keyBytes, password);
      const cmpServers = await cmpServerList(ca);
      const certs = euSign.GetCertificatesByKeyInfo(keyInfo, cmpServers);
      euSign.SaveCertificates(certs);
      euSign.ReadPrivateKeyBinary(keyBytes, password);
    } catch {
      // Кидаємо ПЕРВИННУ помилку читання (зрозуміліша: невірний пароль тощо),
      // якщо і CMP не допоміг.
      throw normalizeErr(e);
    }
  }
  const info = euSign.GetPrivateKeyOwnerInfo();
  const owner: KeyOwnerInfo = {
    subjCN: info.GetSubjCN(),
    subjOrg: safeCall(() => info.GetSubjOrg()),
    subjTitle: safeCall(() => info.GetSubjTitle()),
    issuerCN: info.GetIssuerCN(),
    serial: info.GetSerial(),
    subjDRFOCode: safeCall(() => info.GetSubjDRFOCode()),
    subjEDRPOUCode: safeCall(() => info.GetSubjEDRPOUCode()),
  };
  // Запам'ятати у реєстр (файл+пароль+власник) — щоб перемикатись між кількома
  // ключами без повторного вводу пароля. Дублікат оновлюється.
  registerKey(owner, fileName, keyBytes, password, ca);
  return owner;
}

/** Список CMP-серверів для авто-визначення АЦСК: обраний → або всі з CAs.json. */
async function cmpServerList(ca: EuSignCA | null): Promise<string[]> {
  if (ca?.cmpAddress) return [`${ca.cmpAddress}:80`];
  const cas = await getCAs();
  const out: string[] = [];
  for (const c of cas) {
    if (c.cmpAddress) out.push(`${c.cmpAddress}:80`);
  }
  return out;
}

// Власник АКТИВНОГО ключа (зараз у слоті бібліотеки) — тримається у пам'яті
// вкладки на СЕСІЮ (ключ НЕ покидає браузер, у storage не пишеться).
let _sessionOwner: KeyOwnerInfo | null = null;

// ── Реєстр КІЛЬКОХ зчитаних ключів (для бухгалтерів на кілька юросіб) ──────
// Бібліотека ІІТ тримає лише ОДИН активний ключ у слоті. Тож ми зберігаємо
// файл+пароль+власника кожного зчитаного ключа у пам'яті вкладки, а при виборі
// «підписати цим ключем» — перечитуємо потрібний у слот (activateKey). Пароль
// живе у пам'яті вкладки (як і сам ключ) — не пишеться на диск/у storage.
export interface StoredKey {
  id: string;            // стабільний ідентифікатор запису
  owner: KeyOwnerInfo;   // ПІБ/код/АЦСК — для показу і звірки
  fileName: string;
  keyBytes: Uint8Array;  // вміст файлу ключа (пам'ять вкладки)
  password: string;      // пароль (пам'ять вкладки)
  ca: EuSignCA | null;   // обраний АЦСК (або null=авто) — для перечитування
}

let _keys: StoredKey[] = [];
let _activeKeyId: string | null = null;
let _keyIdSeq = 0;

/** Код особи ключа: ЄДРПОУ (юрособа) АБО РНОКПП/ІПН (ФОП/фізособа). */
export function keyCode(owner: KeyOwnerInfo | null): string {
  if (!owner) return '';
  return (owner.subjEDRPOUCode || owner.subjDRFOCode || '').trim();
}

/** Усі зчитані у сесії ключі (для списку/вибору у панелі). */
export function listKeys(): StoredKey[] {
  return _keys;
}

/** id активного ключа (того, що зараз у слоті бібліотеки). */
export function activeKeyId(): string | null {
  return _activeKeyId;
}

/** Додати ключ у реєстр (після успішного зчитування). Дублікат за кодом+серійником
 *  оновлюється, а не плодиться. Повертає доданий/оновлений запис. */
function registerKey(
  owner: KeyOwnerInfo, fileName: string, keyBytes: Uint8Array,
  password: string, ca: EuSignCA | null,
): StoredKey {
  const dup = _keys.find(
    (k) => keyCode(k.owner) === keyCode(owner) && k.owner.serial === owner.serial);
  const rec: StoredKey = {
    id: dup ? dup.id : `k${++_keyIdSeq}`,
    owner, fileName, keyBytes, password, ca,
  };
  const dupIdx = dup ? _keys.indexOf(dup) : -1;
  if (dupIdx >= 0) _keys[dupIdx] = rec;
  else _keys.push(rec);
  _activeKeyId = rec.id;  // щойно зчитаний — активний
  return rec;
}

/**
 * Зробити ключ активним (перечитати у слот бібліотеки). Потрібно перед
 * будь-якою крипто-операцією, коли зчитано КІЛЬКА ключів. Якщо ключ уже
 * активний — no-op. Повертає власника активного ключа.
 */
export async function activateKey(id: string): Promise<KeyOwnerInfo> {
  const rec = _keys.find((k) => k.id === id);
  if (!rec) throw new Error('Ключ не знайдено у списку зчитаних');
  if (_activeKeyId === id && isKeyReaded()) return rec.owner;
  await loadEuSign();
  applyCASettings(rec.ca);
  resetSlot();
  euSign.ReadPrivateKeyBinary(rec.keyBytes, rec.password);
  _sessionOwner = rec.owner;
  _activeKeyId = id;
  return rec.owner;
}

/** Прибрати ОДИН ключ з реєстру (кнопка × у списку). Якщо він був активний —
 *  скидаємо слот. */
export function removeKey(id: string): void {
  _keys = _keys.filter((k) => k.id !== id);
  if (_activeKeyId === id) {
    resetSlot();
    const last = _keys[_keys.length - 1];
    _activeKeyId = last ? last.id : null;
    _sessionOwner = null;
  }
}

/** Скинути лише слот бібліотеки (без чистки реєстру). */
function resetSlot(): void {
  if (euSign?.IsPrivateKeyReaded?.()) {
    try { euSign.ResetPrivateKey(); } catch { /* no-op */ }
  }
}

export function resetKey(): void {
  resetSlot();
  _sessionOwner = null;
  _keys = [];
  _activeKeyId = null;
}

export function isKeyReaded(): boolean {
  return !!euSign?.IsPrivateKeyReaded?.();
}

/** Власник АКТИВНОГО ключа (у слоті). null якщо жоден ключ не зчитано. */
export function sessionKeyOwner(): KeyOwnerInfo | null {
  return isKeyReaded() ? _sessionOwner : null;
}

/**
 * Крипто-ланцюг подання у ДПС — ЗВІРЕНО З КОДОМ Е-КАБІНЕТУ
 * (cabinet.tax.gov.ua chunk-HKSLCC77: signData + addSignHeaderDp):
 *
 *  1. підпис CAdES-T (SetRuntimeParameter("SignType", 4) — З TSP-міткою);
 *  2. СЕКЦІЯ підпису вручну: 'UA1_SIGN'\\0 + 4 байти довжини (LE) + p7s
 *     (саме так робить Е-кабінет у addSignHeaderDp);
 *  3. шифрування секції на сертифікат ДПС (EnvelopDataEx, signData=false);
 *  4. AppendTransportHeader('UA1', ...) — TRANSPORTABLE-заголовок наказу №499.
 */
const EU_SIGN_TYPE_PARAMETER = 'SignType';
// Наказ МФУ №557 (порядок обміну е-документами з контролюючими органами)
// вимагає підпис з ПОВНИМИ даними перевірки — CAdES-X Long (усередині
// OCSP-відповіді про статус сертифіката). M.E.Doc підписує саме так.
// Прецедент 2026-07-09: наш CAdES-T шлюз ПРИЙМАВ (кв.№1 з розпізнаним
// підписантом), але центральна система не завершувала прийом — «no data in
// Sent» у веб-кабінеті і жодної кв.№2 понад добу. X-Long вимагає робочий
// OCSP видавця ключа (йде через наш проксі, як TSP/CMP); якщо OCSP
// недоступний — fallback на CAdES-T (стара поведінка, гірше не стає).
const EU_SIGN_TYPE_CADES_T = 4;
const EU_SIGN_TYPE_CADES_X_LONG = 16;
const EU_SIGN_TYPE_CADES_BES = 1;

/**
 * Заголовок Authorization для API приватної частини ЕК ДПС
 * (/ws/public_api/post/incoming, .../ta/debt тощо).
 *
 * Формат (докум. cabinet.tax.gov.ua/help/api-registers-int.html): рядок
 * ЄДРПОУ/РНОКПП, підписаний ВНУТРІШНІМ підписом ключа з доданим сертифікатом,
 * BASE64. Це звичайний CAdES-BES (без TSP/OCSP) — тож примусово ставимо
 * SignType=BES (міг лишитись CAdES-T/X-Long після підпису звіту).
 */
export function signApiAuthHeader(edrpou: string): string {
  if (!isKeyReaded()) throw new Error('Спершу зчитайте ключ КЕП');
  euSign.SetRuntimeParameter(EU_SIGN_TYPE_PARAMETER, EU_SIGN_TYPE_CADES_BES);
  const data = new TextEncoder().encode(edrpou.trim());
  const p7s: Uint8Array = euSign.SignDataInternal(true, data, false);
  return bytesToBase64(p7s);
}

/**
 * Режим підпису для подання у ДПС.
 * xlong=true → CAdES-X Long (наказ №557: повні дані перевірки, OCSP увімкнено);
 * xlong=false → CAdES-T (лише TSP-мітка; kvt_by_id і fallback).
 */
export async function prepareDpsSignMode(
  issuerCN: string, xlong = false,
): Promise<void> {
  euSign.SetRuntimeParameter(
    EU_SIGN_TYPE_PARAMETER,
    xlong ? EU_SIGN_TYPE_CADES_X_LONG : EU_SIGN_TYPE_CADES_T,
  );
  const cas = await getCAs();
  const ca = cas.find((c) => c.issuerCNs.some((cn) => cn === issuerCN));
  // TSP видавця ключа (факт: ПриватБанк віддає мітки через наш проксі, 916Б);
  // fallback — TSP КНЕДП ДПС.
  const tsp = euSign.CreateTSPSettings();
  tsp.SetGetStamps(true);
  tsp.SetAddress(ca?.tspAddress || 'ca.tax.gov.ua');
  tsp.SetPort(ca?.tspAddressPort || '80');
  euSign.SetTSPSettings(tsp);
  const ocsp = euSign.CreateOCSPSettings();
  if (xlong) {
    // X-Long: OCSP-статус сертифіката вбудовується у підпис.
    ocsp.SetUseOCSP(true);
    ocsp.SetBeforeStore(true);
    ocsp.SetAddress(ca?.ocspAccessPointAddress || 'czo.gov.ua/services/ocsp/');
    ocsp.SetPort(ca?.ocspAccessPointPort || '80');
  } else {
    ocsp.SetUseOCSP(false);
  }
  euSign.SetOCSPSettings(ocsp);
}

/** Внутрішній КЕП (дані всередині p7s). CAdES-T якщо викликано prepareDpsSignMode. */
export function signDataInternal(data: Uint8Array): Uint8Array {
  if (!isKeyReaded()) throw new Error('Спершу зчитайте особистий ключ');
  return euSign.SignDataInternal(true, data, false);
}

/**
 * Секція наказу №499: '<SIG>'\\0 + довжина (4 байти LE) + дані.
 * SIG = 'UA1_SIGN' (підписаний блок) або 'UA1_CRYPT' (зашифрований блок).
 */
export function wrapSection(sigName: string, payload: Uint8Array): Uint8Array {
  const sig = new TextEncoder().encode(sigName + '\0');
  const n = payload.length;
  const len = new Uint8Array([n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, (n >>> 24) & 0xff]);
  const out = new Uint8Array(sig.length + 4 + n);
  out.set(sig, 0);
  out.set(len, sig.length);
  out.set(payload, sig.length + 4);
  return out;
}

/** Секція UA1_SIGN (підпис) — як addSignHeaderDp Е-кабінету. */
export function wrapUa1SignSection(p7s: Uint8Array): Uint8Array {
  return wrapSection('UA1_SIGN', p7s);
}

/** Секція UA1_CRYPT (зашифрований блок). */
export function wrapUa1CryptSection(enc: Uint8Array): Uint8Array {
  return wrapSection('UA1_CRYPT', enc);
}

/** Зашифрувати (envelope) на сертифікат отримувача (ДПС), БЕЗ вбудованого підпису. */
/**
 * Зашифрувати на сертифікат(и) отримувача.
 * ВАЖЛИВО: другим отримувачем додаємо СЕБЕ (серт шифрування відправника) —
 * інакше ДПС не має на що зашифрувати квитанцію №2 і НЕ формує її («власник
 * ключа відсутній у списку отримувачів»). Медок робить так само.
 */
export function envelopToCert(certBytes: Uint8Array, data: Uint8Array): Uint8Array {
  if (!isKeyReaded()) throw new Error('Спершу зчитайте особистий ключ');
  const info = euSign.ParseCertificate(certBytes);
  euSign.SaveCertificate(certBytes);
  const issuers = [info.issuer];
  const serials = [info.serial];
  // Додати власний серт шифрування (key agreement) як другого отримувача.
  const own = getOwnAgreementCertificate();
  if (own) {
    const oi = euSign.ParseCertificate(own);
    euSign.SaveCertificate(own);
    issuers.push(oi.issuer);
    serials.push(oi.serial);
  }
  // signData=false — НЕ міняємо структуру конверта (приймання вже працює);
  // достатньо ДРУГОГО отримувача (нас), щоб ДПС мала на що шифрувати кв.№2.
  return euSign.EnvelopDataEx(issuers, serials, false, data, false);
}

/**
 * Сформувати encryptedId для /kvt_by_id — id пакета у ПОВНОМУ транспортному
 * контейнері наказу №499 (ідентично contentBase64 звіту, який ДПС приймає):
 * CAdES-T підпис id → секція UA1_SIGN → шифрування на серт ДПС → секція
 * UA1_CRYPT → **TRANSPORTABLE-заголовок**. Без заголовка ДПС відповідала
 * NOT_KVT (id не знаходився); голий envelope без секцій — ERROR_DECRYPT.
 * fname — ім'я файлу вихідного звіту (як у транспортному заголовку подання).
 */
export async function encryptReportId(
  reportId: number | string, fname?: string,
): Promise<string> {
  if (!isKeyReaded()) throw new Error('Спершу зчитайте ключ КЕП');
  // Той самий режим підпису, що й у подання звіту (CAdES-T + TSP видавця).
  await prepareDpsSignMode(_sessionOwner?.issuerCN ?? '');
  const idBytes = new TextEncoder().encode(String(reportId));
  const p7s = signDataInternal(idBytes);
  const signSection = wrapUa1SignSection(p7s);
  const cert = await fetchDpsEncryptionCert();
  const encrypted = envelopToCert(cert, signSection);
  const cryptSection = wrapUa1CryptSection(encrypted);
  const container = appendTransportHeader(
    cryptSection, fname ?? `${reportId}.xml`, '',
    getOwnCertificate(),
  );
  return bytesToBase64(container);
}

/**
 * Повний транспортний контейнер для подання у ДПС (наказ №499):
 * CAdES-T підпис → UA1_SIGN → шифрування на серт ДПС → UA1_CRYPT →
 * TRANSPORTABLE (серт ШИФРУВАННЯ власника + email організації).
 * Спільний для панелі документа і Кабінету ДПС («підписати»).
 */
export async function buildSignedContainer(
  bytes: Uint8Array, filename: string, issuerCN: string, email: string,
): Promise<Uint8Array> {
  // Наказ №557: CAdES-X Long (повні дані перевірки). Якщо OCSP видавця
  // недоступний — fallback CAdES-T (шлюз приймає, стара поведінка).
  let p7s: Uint8Array;
  try {
    await prepareDpsSignMode(issuerCN, true);
    p7s = signDataInternal(bytes);
    console.info('Підпис: CAdES-X Long (повні дані перевірки, наказ №557)');
  } catch (e) {
    console.warn('CAdES-X Long не вдався (OCSP?), fallback CAdES-T:', e);
    await prepareDpsSignMode(issuerCN, false);
    p7s = signDataInternal(bytes);
  }
  const signSection = wrapUa1SignSection(p7s);
  const cert = await fetchDpsEncryptionCert();
  const encrypted = envelopToCert(cert, signSection);
  const cryptSection = wrapUa1CryptSection(encrypted);
  const ownCert = getOwnAgreementCertificate() ?? getOwnCertificate();
  // Ім'я файлу — як є (…xml нижнім, як пише сама ДПС), БЕЗ upper-case.
  return appendTransportHeader(cryptSection, filename, email, ownCert);
}

/**
 * ДІАГНОСТИКА /kvt_by_id: варіанти формату encryptedId (точний формат ДПС
 * не документує — «encryptedId: String»). Перебираються по черзі, доки ДПС
 * не відповість чимось, окрім NOT_KVT. Після встановлення робочого формату
 * зайві варіанти прибрати, лишити робочий у encryptReportId.
 */
export interface EncIdVariant {
  label: string;
  build: () => Promise<string>;
}

export function encryptReportIdVariants(
  reportId: number | string, fname?: string,
): EncIdVariant[] {
  const idStr = String(reportId);
  const enc = (s: string) => new TextEncoder().encode(s);
  const withMode = async () => {
    await prepareDpsSignMode(_sessionOwner?.issuerCN ?? '');
  };
  const layers = async (payload: Uint8Array) => {
    const p7s = signDataInternal(payload);
    const signSection = wrapUa1SignSection(p7s);
    const cert = await fetchDpsEncryptionCert();
    const encrypted = envelopToCert(cert, signSection);
    return wrapUa1CryptSection(encrypted);
  };
  return [
    {
      label: 'повний контейнер (SIGN+CRYPT+TRANSPORT)',
      build: async () => {
        await withMode();
        const cs = await layers(enc(idStr));
        return bytesToBase64(appendTransportHeader(
          cs, fname ?? `${idStr}.xml`, '', getOwnCertificate()));
      },
    },
    {
      label: 'секції без TRANSPORT (SIGN+CRYPT)',
      build: async () => {
        await withMode();
        return bytesToBase64(await layers(enc(idStr)));
      },
    },
    {
      label: 'підпис+шифр без секцій (env(p7s(id)))',
      build: async () => {
        await withMode();
        const p7s = signDataInternal(enc(idStr));
        const cert = await fetchDpsEncryptionCert();
        return bytesToBase64(envelopToCert(cert, p7s));
      },
    },
    {
      label: 'лише шифрування (env(id))',
      build: async () => {
        const cert = await fetchDpsEncryptionCert();
        return bytesToBase64(envelopToCert(cert, enc(idStr)));
      },
    },
    {
      label: 'повний контейнер, вміст = fname',
      build: async () => {
        await withMode();
        const cs = await layers(enc(fname ?? `${idStr}.xml`));
        return bytesToBase64(appendTransportHeader(
          cs, fname ?? `${idStr}.xml`, '', getOwnCertificate()));
      },
    },
  ];
}

/**
 * Сертифікат шифрування для КАНАЛУ ПОДАННЯ.
 * Ми шлемо через REST API Е-кабінету → шифруємо на ЙОГО сертифікат EK_C_2025
 * («сертифікати API Електронного кабінету для... шифрування» — ДПС).
 * STS_2025_2 — для іншого каналу (SOAP «Єдине вікно»/email), НЕ для API ЕК.
 */
export function fetchDpsEncryptionCert(): Promise<Uint8Array> {
  return fetchBytes(`${ASSETS}/EK_C_2025.cer`);
}

/**
 * Обгорнути підписані+зашифровані дані у транспортний контейнер ДПС
 * (наказ №499): бібліотека ІІТ додає заголовок TRANSPORTABLE сама.
 * caType — CERTYPE АЦСК ('UA1' типово); email — e-mail відправника (може
 * бути порожнім); clientCert — сертифікат підписанта (для CRC/CERTYPE).
 */
export function appendTransportHeader(
  data: Uint8Array, fileName: string, email: string,
  clientCert: Uint8Array, caType = 'UA1',
): Uint8Array {
  return euSign.AppendTransportHeader(caType, fileName, email, clientCert, data);
}

/** Сертифікат поточного зчитаного ключа (для транспортного заголовка). */
export function getOwnCertificate(): Uint8Array {
  // Перший власний сертифікат підписанта (DSTU4145, для підпису)
  const info = euSign.EnumOwnCertificates(0);
  if (!info) throw new Error('Сертифікат ключа не знайдено');
  return euSign.GetCertificate(info.GetIssuer(), info.GetSerial());
}

// keyUsageType сертифіката (бітова маска, euscpt.js).
const KEY_USAGE_DIGITAL_SIGNATURE = 0x0001;
const KEY_USAGE_KEY_AGREEMENT = 0x0010;

export interface OwnCertInfo {
  issuer: string;
  serial: string;
  keyUsage: string;       // людський опис («ЕЦП», «Протоколи розподілу ключів»)
  isSignature: boolean;
  isKeyAgreement: boolean;
}

/** Усі власні сертифікати зчитаного ключа (підпис + шифрування, якщо є). */
export function listOwnCertificates(): OwnCertInfo[] {
  const out: OwnCertInfo[] = [];
  for (let i = 0; i < 8; i++) {
    let info: any;
    try {
      info = euSign.EnumOwnCertificates(i);
    } catch {
      break;
    }
    if (!info) break;
    const kut = Number(info.GetKeyUsageType?.() ?? 0);
    out.push({
      issuer: info.GetIssuer(),
      serial: info.GetSerial(),
      keyUsage: String(info.GetKeyUsage?.() ?? ''),
      isSignature: (kut & KEY_USAGE_DIGITAL_SIGNATURE) !== 0
        || /підпис|signature/i.test(String(info.GetKeyUsage?.() ?? '')),
      isKeyAgreement: (kut & KEY_USAGE_KEY_AGREEMENT) !== 0
        || /розподіл|agreement/i.test(String(info.GetKeyUsage?.() ?? '')),
    });
  }
  return out;
}

/**
 * Сертифікат ШИФРУВАННЯ (key agreement) власника ключа — саме на нього ДПС
 * шифрує квитанцію №2/відповідь. Якщо його нема у ключі — null (тоді ДПС
 * не має на що шифрувати відповідь — ймовірна причина «кв.№2 не приходить»).
 */
export function getOwnAgreementCertificate(): Uint8Array | null {
  for (let i = 0; i < 8; i++) {
    let info: any;
    try {
      info = euSign.EnumOwnCertificates(i);
    } catch {
      break;
    }
    if (!info) break;
    const kut = Number(info.GetKeyUsageType?.() ?? 0);
    const usage = String(info.GetKeyUsage?.() ?? '');
    if ((kut & KEY_USAGE_KEY_AGREEMENT) !== 0
        || /розподіл|agreement/i.test(usage)) {
      return euSign.GetCertificate(info.GetIssuer(), info.GetSerial());
    }
  }
  return null;
}

/**
 * Розібрати квитанцію ДПС до тексту (XML).
 *
 * Квитанція — багатошарова (наказ №499): секції 'UA1_SIGN'\\0+len+p7s
 * (підпис ДПС) та 'UA1_CRYPT'\\0+len+enc (шифр на наш ключ, лише кв.№2).
 * Знімаємо шар за шаром: CRYPT → DevelopData (розшифрувати нашим ключем);
 * SIGN → GetDataFromSignedData (витягти дані з підпису, БЕЗ online-перевірки);
 * доки не дійдемо до XML (<?xml / <DECLAR). Перевірено фактом на кв.№1:
 * UA1_SIGN → CAdES (PKCS#7 SignedData) → XML усередині.
 */
export function developReceipt(receiptBase64: string): string {
  const bytes = base64ToBytes(receiptBase64);
  return bytesToString(unwrapReceipt(bytes, 0));
}

/**
 * ДІАГНОСТИКА: розгорнути НАШ вихідний контейнер (те, що ми шлемо в ДПС)
 * так, як це робить приймальний шлюз — щоб побачити реальний вміст і
 * перевірити підпис. Знімає TRANSPORTABLE-заголовок → UA1_CRYPT (розшифр.
 * нашим ключем) → UA1_SIGN → XML. Повертає XML + інфо про підпис.
 */
export function diagnoseOutgoingContainer(containerBase64: string): {
  xml: string; signInfo: string;
} {
  if (!isKeyReaded()) throw new Error('Спершу зчитайте ключ КЕП');
  let bytes = base64ToBytes(containerBase64);
  // Зняти TRANSPORTABLE\0 + 4 байти довжини (LE) + заголовок.
  const magic = new TextDecoder('ascii').decode(bytes.subarray(0, 13));
  if (magic === 'TRANSPORTABLE') {
    const hlen = bytes[14]! | (bytes[15]! << 8) | (bytes[16]! << 16) | (bytes[17]! << 24);
    bytes = bytes.subarray(18 + hlen);
  }
  // UA1_CRYPT → розшифрувати нашим ключем.
  let sec = parseSection(bytes);
  if (sec?.sig === 'UA1_CRYPT') {
    const dev = euSign.DevelopData(sec.payload);
    bytes = toBytes(dev?.GetData ? dev.GetData() : dev);
  }
  // UA1_SIGN → перевірити підпис (VerifyDataInternal) + витягти дані.
  let signInfo = '(підпис не знайдено)';
  sec = parseSection(bytes);
  const p7s = sec?.sig === 'UA1_SIGN' ? sec.payload : bytes;
  try {
    const vi = euSign.VerifyDataInternal(p7s);
    const s = vi?.GetSignInfo ? vi.GetSignInfo() : vi;
    const t = s?.GetSignType ? s.GetSignType() : '?';
    const who = s?.GetSubjCN ? s.GetSubjCN() : '';
    signInfo = `Підпис ВАЛІДНИЙ. Тип=${t}; підписант=${who}`;
    bytes = toBytes(vi?.GetData ? vi.GetData() : euSign.GetDataFromSignedData(p7s));
  } catch (e: any) {
    signInfo = `Підпис НЕ пройшов перевірку: ${e?.message ?? e}`;
    try { bytes = toBytes(euSign.GetDataFromSignedData(p7s)); } catch { /* — */ }
  }
  return { xml: bytesToString(unwrapReceipt(bytes, 0)), signInfo };
}

export interface ParsedReceipt {
  kind: string;         // «Квитанція №1 (доставка)» / «Квитанція №2 (обробка)»
  accepted: boolean | null;  // прийнято / відхилено / невідомо
  text: string;         // читабельний текст рішення/повідомлення органу
  rawXml: string;       // повний XML (для «показати деталі»)
}

/**
 * Розібрати квитанцію ДПС у читабельний вигляд. Квитанція — XML DECLAR:
 * C_DOC_SUB '991' — доставка (кв.№1), '801'/'802' — обробка (кв.№2).
 * Текст рішення органу — у тегах тіла (шукаємо змістовні текстові вузли).
 */
export function parseReceipt(receiptBase64: string): ParsedReceipt {
  const xml = developReceipt(receiptBase64);
  let doc: Document | null = null;
  try {
    doc = new DOMParser().parseFromString(xml, 'text/xml');
  } catch { /* лишимо як текст */ }

  const sub = doc?.querySelector('DECLARHEAD C_DOC_SUB')?.textContent?.trim() ?? '';
  const isKvt1 = sub === '991';
  const kind = isKvt1 ? 'Квитанція №1 (доставка)' : 'Квитанція №2 (обробка)';

  // Основне рішення органу — тег HRESULT; додатково TEXT/HDOCNAME.
  const pick = (sel: string) =>
    (doc?.querySelector(sel)?.textContent ?? '').trim();
  const result = pick('HRESULT');
  const bodyText = pick('TEXT');
  const docName = pick('HDOCNAME');
  const parts = [result, bodyText].filter((t) => t.length >= 3);
  const text = parts.length
    ? parts.join('\n\n')
    : (isKvt1 ? 'Документ доставлено до контролюючого органу.' : (docName || xml));

  let accepted: boolean | null = null;
  const low = (result + ' ' + bodyText).toLowerCase();
  if (/прийнято|зареєстров|збережено/.test(low)) accepted = isKvt1 ? null : true;
  else if (/не прийнято|відмов|відхил|помилк/.test(low)) accepted = false;

  return { kind, accepted, text, rawXml: xml };
}

/** Структуровані поля квитанції для друкованого бланка (форма J1499202). Усе
 *  береться з тегів XML квитанції — той самий вміст, що показує Е-кабінет. */
export interface ReceiptFields {
  num: 1 | 2;
  tin: string;          // ЄДРПОУ/РНОКПП платника (HTIN)
  name: string;         // найменування платника (HNAME)
  email: string;        // HEMAIL
  docKod: string;       // код форми документа (HDOCKOD)
  docName: string;      // назва документа звітності (HDOCNAME)
  fileName: string;     // ім'я файлу (HFILENAME)
  docStan: string;      // стан документа: Звітна/Уточнююча (HDOCSTAN)
  period: string;       // назва звітного періоду (HPERIOD)
  year: string;         // звітний рік (HZY)
  term: string;         // граничний термін подання (HDTERM)
  bosCode: string;      // РНОКПП керівника/підписанта (HKBOS)
  bos: string;          // ПІБ керівника/підписанта (HBOS)
  buhCode: string;      // РНОКПП бухгалтера (HKBUH)
  buh: string;          // ПІБ бухгалтера (HBUH)
  orgCode: string;      // код контролюючого органу (HKODSTI)
  stiName: string;      // назва контролюючого органу (HSTI)
  result: string;       // HRESULT — результат обробки
  date: string;         // HDATE
  time: string;         // HTIME
  regNum: string;       // реєстраційний № (HNUMREG)
  sender: string;       // відправник (HSENDER)
  text: string;         // TEXT — розгорнутий текст рішення/попереджень
  accepted: boolean | null;
}

function fmtDgDate(v: string): string {
  // DGDate у квитанції — 8 цифр ДДММРРРР → ДД.ММ.РРРР
  const m = v.match(/^(\d{2})(\d{2})(\d{4})$/);
  return m ? `${m[1]}.${m[2]}.${m[3]}` : v;
}

/** Розшифрувати квитанцію ключем і повернути (поля + сирий XML). XML можна
 *  закешувати на сервері, щоб далі будувати бланк БЕЗ ключа. */
export function developReceiptToXml(receiptBase64: string): string {
  return developReceipt(receiptBase64);
}

export function parseReceiptFields(receiptBase64: string): ReceiptFields {
  return parseReceiptFieldsFromXml(developReceipt(receiptBase64));
}

/** Ті самі поля, але з уже РОЗШИФРОВАНОГО XML (кеш receiptN_xml) — без ключа. */
export function parseReceiptFieldsFromXml(xml: string): ReceiptFields {
  let doc: Document | null = null;
  try { doc = new DOMParser().parseFromString(xml, 'text/xml'); } catch { /* */ }
  const g = (sel: string) => (doc?.querySelector(sel)?.textContent ?? '').trim();

  const sub = g('DECLARHEAD C_DOC_SUB');
  const num: 1 | 2 = sub === '991' ? 1 : 2;
  const result = g('HRESULT');
  const bodyText = g('TEXT');
  const low = (result + ' ' + bodyText).toLowerCase();
  let accepted: boolean | null = null;
  if (/прийнято|зареєстров|збережено|виконано вдало/.test(low)) accepted = num === 1 ? null : true;
  else if (/не прийнято|відмов|відхил|помилк/.test(low)) accepted = false;

  return {
    num,
    tin: g('HTIN'),
    name: g('HNAME'),
    email: g('HEMAIL'),
    docKod: g('HDOCKOD'),
    docName: g('HDOCNAME'),
    fileName: g('HFILENAME'),
    docStan: g('HDOCSTAN'),
    period: g('HPERIOD'),
    year: g('HZY'),
    term: fmtDgDate(g('HDTERM')),
    bosCode: g('HKBOS'),
    bos: g('HBOS'),
    buhCode: g('HKBUH'),
    buh: g('HBUH'),
    orgCode: g('HKODSTI'),
    stiName: g('HSTI'),
    result,
    date: fmtDgDate(g('HDATE')),
    time: g('HTIME'),
    regNum: g('HNUMREG'),
    sender: g('HSENDER'),
    text: bodyText,
    accepted,
  };
}

function unwrapReceipt(data: Uint8Array, depth: number): Uint8Array {
  if (depth > 6) throw new Error('Занадто багато шарів квитанції');
  // Дійшли до XML — це вміст.
  if (looksLikeXml(data)) return data;

  const sec = parseSection(data);
  if (sec) {
    if (sec.sig === 'UA1_CRYPT') {
      if (!isKeyReaded()) throw new Error('Спершу зчитайте ключ КЕП');
      const dev = euSign.DevelopData(sec.payload);
      const out = dev?.GetData ? dev.GetData() : dev;
      return unwrapReceipt(toBytes(out), depth + 1);
    }
    if (sec.sig === 'UA1_SIGN') {
      const inner = euSign.GetDataFromSignedData(sec.payload);
      return unwrapReceipt(toBytes(inner), depth + 1);
    }
    // Невідома секція — спробувати payload далі.
    return unwrapReceipt(sec.payload, depth + 1);
  }

  // Не секція: можливо чистий CMS (SignedData/EnvelopedData) без обгортки.
  try {
    const inner = euSign.GetDataFromSignedData(data);
    return unwrapReceipt(toBytes(inner), depth + 1);
  } catch { /* не підпис */ }
  try {
    if (isKeyReaded()) {
      const dev = euSign.DevelopData(data);
      const out = dev?.GetData ? dev.GetData() : dev;
      return unwrapReceipt(toBytes(out), depth + 1);
    }
  } catch { /* не шифр */ }
  return data;  // віддаємо як є (краще показати сирі байти, ніж впасти)
}

/** Секція наказу №499: '<SIG>'\\0 + 4 байти довжини (LE) + payload. */
function parseSection(data: Uint8Array): { sig: string; payload: Uint8Array } | null {
  const z = data.indexOf(0);
  if (z <= 0 || z > 16) return null;
  const sig = new TextDecoder('ascii').decode(data.subarray(0, z));
  if (sig !== 'UA1_SIGN' && sig !== 'UA1_CRYPT') return null;
  if (data.length < z + 5) return null;
  const len = data[z + 1]! | (data[z + 2]! << 8) | (data[z + 3]! << 16) | (data[z + 4]! << 24);
  const start = z + 5;
  return { sig, payload: data.subarray(start, start + len) };
}

function looksLikeXml(data: Uint8Array): boolean {
  // Пропустити можливий BOM/пробіли, шукати '<'
  for (let i = 0; i < Math.min(data.length, 8); i++) {
    const c = data[i]!;
    if (c === 0x3c) return true;  // '<'
    if (c !== 0xef && c !== 0xbb && c !== 0xbf && c !== 0x20 && c !== 0x0a && c !== 0x0d) break;
  }
  return false;
}

function toBytes(v: any): Uint8Array {
  if (v instanceof Uint8Array) return v;
  if (Array.isArray(v)) return new Uint8Array(v);
  if (typeof v === 'string') return base64ToBytes(v);
  if (v?.buffer) return new Uint8Array(v.buffer);
  return new Uint8Array(0);
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr;
}

export function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}

function bytesToString(bytes: Uint8Array): string {
  try {
    return new TextDecoder('windows-1251' as any).decode(bytes);
  } catch {
    return new TextDecoder().decode(bytes);
  }
}

function safeCall<T>(fn: () => T): T | undefined {
  try { return fn(); } catch { return undefined; }
}

function normalizeErr(e: any): Error {
  const msg = typeof e === 'string' ? e
    : e?.message ?? (e?.GetMessage ? e.GetMessage() : String(e));
  return new Error(msg);
}
